import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import "../styles/InventoryChart.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function InventoryChart() {
  const [products, setProducts] = useState([]);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Inventory Levels',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Function to generate vibrant colors
  const generateColors = (count) => {
    const colors = [];
    const backgroundColors = [];
    
    for (let i = 0; i < count; i++) {
      // Generate hue values evenly distributed around the color wheel
      const hue = (i * 360) / count;
      const color = `hsl(${hue}, 70%, 50%)`;
      const backgroundColor = `hsla(${hue}, 70%, 50%, 0.6)`;
      
      colors.push(color);
      backgroundColors.push(backgroundColor);
    }
    
    return { colors, backgroundColors };
  };

  useEffect(() => {
    fetchProducts();
    fetchInventoryRecords();
  }, []);

  useEffect(() => {
    if (inventoryRecords.length > 0) {
      // Sort records by date
      const sortedRecords = [...inventoryRecords].sort((a, b) => 
        new Date(b.DateOfGard) - new Date(a.DateOfGard)
      );

      // Set available dates
      const dates = sortedRecords.map(record => {
        const date = new Date(record.DateOfGard);
        return {
          value: record.DateOfGard,
          label: `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
        };
      });
      setAvailableDates(dates);

      // Set latest record as default
      if (!selectedDate) {
        setSelectedDate(sortedRecords[0].DateOfGard);
        setSelectedRecord(sortedRecords[0]);
      }
    }
  }, [inventoryRecords]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/products/getProduct"
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchInventoryRecords = async () => {
    try {
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/inventory?page=1&limit=100"
      );
      if (!response.ok) throw new Error("Failed to fetch inventory records");
      const data = await response.json();
      setInventoryRecords(data.data?.inventory || []);
    } catch (error) {
      console.error("Error fetching inventory records:", error);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    const record = inventoryRecords.find(r => r.DateOfGard === date);
    setSelectedRecord(record);
  };

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!selectedRecord) return { labels: [], datasets: [] };
    
    const productLabels = selectedRecord.Products.map(item => {
      const productId = typeof item.ProductID === 'object' ? 
                       (item.ProductID._id || item.ProductID.id) : 
                       item.ProductID;
      const product = products.find(p => p._id === productId);
      return product ? `${product.ProductNameEN}` : 'Unknown Product';
    });
    
    const productCounts = selectedRecord.Products.map(item => item.Count);
    const { colors, backgroundColors } = generateColors(productLabels.length);
    
    return {
      labels: productLabels,
      datasets: [
        {
          label: 'Quantity',
          data: productCounts,
          backgroundColor: backgroundColors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [selectedRecord, products]);

  // Prepare table data with product details
  const getProductDetails = () => {
    if (!selectedRecord) return [];
    
    return selectedRecord.Products.map(item => {
      const productId = typeof item.ProductID === 'object' ? 
                       (item.ProductID._id || item.ProductID.id) : 
                       item.ProductID;
      const product = products.find(p => p._id === productId);
      return {
        nameEN: product ? product.ProductNameEN : 'Unknown Product',
        nameAR: product ? product.ProductNameAR : 'Unknown Product',
        count: item.Count
      };
    });
  };

  return (
    <div className="inventory-chart-container">
      <div className="chart-header">
        <h2>Inventory Chart View</h2>
        <div className="date-selector">
          <label>Select Date:</label>
          <select 
            value={selectedDate} 
            onChange={handleDateChange}
            className="form-select"
          >
            {availableDates.map(date => (
              <option key={date.value} value={date.value}>
                {date.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="chart-section">
        <Bar options={options} data={chartData} />
      </div>

      <div className="inventory-details mt-4">
        <h3>Detailed Inventory</h3>
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Product (English)</th>
                <th>Product (Arabic)</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {getProductDetails().map((product, index) => (
                <tr key={index}>
                  <td>{product.nameEN}</td>
                  <td>{product.nameAR}</td>
                  <td>{product.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default InventoryChart; 